import * as React from 'react';
import {FunctionComponent, useMemo} from 'react';
import {PlainGraph} from './PlainGraph';
import {Stroke} from '../Models/Stroke';
import {formatPower} from '../Ui/Support/Formatting/formatPower';
import {formatDuration} from '../Ui/Support/Formatting/formatDuration';
import {formatDistance} from '../Ui/Support/Formatting/formatDistance';
import {XAxisUnit, getXAxisUnitInnerProperty} from './support/variableXAxis';
import {lazyTake, strokesToGraphableStrokes, strokesToGraphData} from './support/strokesToGraphData';

interface OwnProps {
    strokes: Stroke[];
    xAxisUnit: XAxisUnit;
    tooltip?: {point: {x: number; y: number; label?: string}; x: number} | null;
    onDataActivated?: (data: {point: {x: number; y: number; label?: string}; x: number}) => void;
}

export const PowerGraph: FunctionComponent<OwnProps> = ({strokes, xAxisUnit, tooltip, onDataActivated}) => {
    if (strokes.length === 0) {
        return <div className="graphs__empty">No stroke data available</div>;
    }

    if (strokes.some((stroke) => stroke.power === null)) {
        return <div className="graphs__empty">No power data available</div>;
    }

    // Create axis and values from strokes.
    const graphableStrokes = useMemo(() => strokesToGraphableStrokes(strokes), [strokes]);
    const graphData = useMemo(() => {
        return strokesToGraphData(
            graphableStrokes,
            xAxisUnit,
            (stroke) => stroke.power!.watts,
            (watts) => Math.round(watts).toString(),
        );
    }, [strokes, xAxisUnit]);
    const tooltipStroke = graphableStrokes.find(
        (stroke) => lazyTake(stroke, getXAxisUnitInnerProperty(xAxisUnit)) === tooltip?.point.x,
    );

    return (
        <PlainGraph
            color="#6610F2"
            height={80}
            padding={{top: 0, right: 0, bottom: 20, left: 20}}
            axis={graphData.axis}
            values={graphData.values}
            tooltip={
                tooltip &&
                tooltipStroke && {
                    x: tooltip.x,
                    content: [
                        formatPower(tooltipStroke?.power ?? null).unit +
                            ': ' +
                            formatPower(tooltipStroke?.power ?? null).formatted,
                        'Time: ' + formatDuration(tooltipStroke?.totalTime ?? null).formatted,
                        'Dist: ' +
                            formatDistance(tooltipStroke?.totalDistance ?? null).formatted +
                            ' ' +
                            formatDistance(tooltipStroke?.totalDistance ?? null).unit,
                    ].join('\n'),
                }
            }
            onDataActivated={onDataActivated}
        />
    );
};
